import { useEffect, useRef, useState, type ReactNode } from "react";
import IndustriesDropdown from "@/components/ernest/IndustriesDropdown";
import { Helmet } from "react-helmet-async";
import socialPreview from "@/assets/social-preview.png.asset.json";
import { Link, useLocation } from "react-router-dom";
import { animate, motion, useInView, useReducedMotion } from "framer-motion";
import { Sparkles, Target, BarChart3, Zap, Check, Linkedin, Mail, MapPin, Leaf, Sun, ShieldCheck, Search, TrendingUp, Sprout, Camera, Award, CalendarDays, X, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { openCookieSettings } from "@/lib/consent";


interface BlogPostPreview {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  published_at: string | null;
  tags: string[] | null;
}

const fadeUp = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.6, ease: "easeOut" as const } },
};



/* ─── Floating card helper ─────────────────────────────────── */
const FloatingCard = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3, margin: MODULE_VIEWPORT_MARGIN }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className={`bg-white rounded-2xl border border-border/60 shadow-[0_8px_30px_-12px_rgba(20,20,40,0.12)] ${className}`}
  >
    {children}
  </motion.div>
);

/* ─── Scroll-triggered motion for the product mockups ──────────
   Everything plays once, when the mockup scrolls into view, then rests on its
   final state. With reduced motion on, the final state shows straight away. */
const MODULE_VIEWPORT_MARGIN = "0px 0px -15% 0px";

const useReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4, margin: MODULE_VIEWPORT_MARGIN });
  const reduced = !!useReducedMotion();
  return { ref, started: inView, reduced };
};

const CountUp = ({
  to,
  decimals = 0,
  suffix = "",
  group = false,
  delay = 0,
}: {
  to: number;
  decimals?: number;
  suffix?: string;
  group?: boolean;
  delay?: number;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5, margin: MODULE_VIEWPORT_MARGIN });
  const reduced = !!useReducedMotion();
  // Starts on the final value so prerendered HTML and reduced-motion users see
  // the real number; the client drops to 0 just before the count-up plays.
  const [value, setValue] = useState(to);
  useEffect(() => {
    if (!reduced) setValue(0);
  }, [reduced]);
  useEffect(() => {
    if (!inView || reduced) return;
    const controls = animate(0, to, { duration: 1.4, delay, ease: "easeOut", onUpdate: setValue });
    return () => controls.stop();
  }, [inView, reduced, to, delay]);
  const text = group ? Math.round(value).toLocaleString("en-US") : value.toFixed(decimals);
  return <span ref={ref}>{text}{suffix}</span>;
};

const GrowBar = ({ pct, delay = 0.5 }: { pct: number; delay?: number }) => {
  const reduced = !!useReducedMotion();
  return (
    <motion.div
      className="h-full rounded-full bg-emerald-500"
      initial={reduced ? false : { width: "0%" }}
      whileInView={{ width: `${pct}%` }}
      viewport={{ once: true, amount: 0.5, margin: MODULE_VIEWPORT_MARGIN }}
      transition={{ duration: 1.4, delay, ease: "easeOut" }}
      style={reduced ? { width: `${pct}%` } : undefined}
    />
  );
};

const MEASURE_METRICS = [
  { label: "Organic Reach", to: 14.2, decimals: 1, suffix: "K" },
  { label: "Word of Mouth", to: 3400, group: true },
  { label: "Purchase Intent", to: 67, suffix: "%" },
];

const VALUES = ["Performance", "Authenticity", "Impact", "Exclusivity", "Creativity", "Innovation"];
const VALUES_PICKED = ["Authenticity", "Innovation"];

const ValueChips = () => {
  const { ref, started, reduced } = useReveal();
  const [on, setOn] = useState<string[]>(reduced ? VALUES_PICKED : []);
  useEffect(() => {
    if (reduced) return setOn(VALUES_PICKED);
    if (!started) return;
    const timers = VALUES_PICKED.map((v, i) =>
      setTimeout(() => setOn((p) => (p.includes(v) ? p : [...p, v])), 2200 + i * 800),
    );
    return () => timers.forEach(clearTimeout);
  }, [started, reduced]);
  return (
    <div ref={ref} className="grid grid-cols-2 gap-1">
      {VALUES.map((f) => {
        const picked = on.includes(f);
        return (
          <motion.span
            key={f}
            animate={{ scale: picked && !reduced ? [1, 1.15, 1] : 1 }}
            transition={{ duration: 0.5 }}
            className={`px-1.5 py-0.5 rounded-full border text-[8px] md:text-[9px] font-medium text-center whitespace-nowrap transition-colors duration-500 ${
              picked ? "bg-[#584dff]/15 border-[#584dff]/30 text-[#584dff]" : "border-border/60 bg-muted/40"
            }`}
            style={picked ? undefined : { color: "#292939" }}
          >
            {f}
          </motion.span>
        );
      })}
    </div>
  );
};

const FORMATS = [
  { icon: Sprout, label: "Product", desc: "Product seeding" },
  { icon: CalendarDays, label: "Events", desc: "Event sponsorship" },
  { icon: Award, label: "Affiliation", desc: "Ambassador programs" },
  { icon: Camera, label: "Content", desc: "Branded content" },
];
// Events, Content, Product: the three activations the campaign ends up with.
const FORMATS_PICKED = [1, 3, 0];

const ActivationFormats = ({ compact = false }: { compact?: boolean }) => {
  const { ref, started, reduced } = useReveal();
  const [picked, setPicked] = useState<number[]>(reduced ? FORMATS_PICKED : []);
  useEffect(() => {
    if (reduced) return setPicked(FORMATS_PICKED);
    if (!started) return;
    const timers = FORMATS_PICKED.map((idx, i) =>
      setTimeout(() => setPicked((p) => (p.includes(idx) ? p : [...p, idx])), 900 + i * 700),
    );
    return () => timers.forEach(clearTimeout);
  }, [started, reduced]);
  return (
    <div ref={ref} className="space-y-0.5">
      {FORMATS.map(({ icon: Icon, label, desc }, i) => (
        <div
          key={label}
          className={`flex items-center rounded-md -mx-1.5 px-1.5 py-0.5 transition-colors duration-500 ${
            compact ? "gap-2" : "gap-2.5"
          } ${picked.includes(i) ? "bg-[#584dff]/10" : ""}`}
        >
          <div className={`${compact ? "w-5 h-5" : "w-6 h-6"} rounded-md bg-[hsl(var(--ernest-neon))]/20 flex items-center justify-center`}>
            <Icon className={`${compact ? "w-2.5 h-2.5" : "w-3 h-3"} text-foreground`} strokeWidth={1.5} />
          </div>
          <div>
            <p className={`${compact ? "text-[10px]" : "text-xs"} font-medium text-foreground leading-tight`}>{label}</p>
            <p className={`${compact ? "text-[8px]" : "text-[10px]"} text-muted-foreground`}>{desc}</p>
          </div>
          <Check
            className={`ml-auto w-3 h-3 text-[#584dff] transition-opacity duration-500 ${picked.includes(i) ? "opacity-100" : "opacity-0"}`}
            strokeWidth={2.5}
          />
        </div>
      ))}
    </div>
  );
};

/* ─── Product module compositions ──────────────────────────── */

const SB = "https://gcbavizlaxatiafdkaiy.supabase.co/storage/v1/object/public/community-images-homepage";

// plainAnswer feeds the FAQPage JSON-LD (needs plain text even where `a`
// renders as JSX); keep it in sync with `a` by hand for the JSX-answer items.
const FAQ_ITEMS: { q: string; a: ReactNode; plainAnswer: string }[] = [
  {
    q: "Is ernest a platform, an agency, or a marketplace?",
    a: (
      <>
        <p>
          Think of ernest as your end-to-end operating system for community marketing. Brief our team and we run the activation with you. You gain access to the app: discover communities, track every activation, see what's working.
        </p>
        <p>
          <strong className="font-medium text-foreground">Kithe</strong> is our community marketplace, where communities log in, create a profile, and make themselves discoverable to brands looking to partner with them.
        </p>
        <p>
          Both sides work together as one unified system.
        </p>
      </>
    ),
    plainAnswer: "Think of ernest as your end-to-end operating system for community marketing. Brief our team and we run the activation with you. You gain access to the app: discover communities, track every activation, see what's working. Kithe is our community marketplace, where communities log in, create a profile, and make themselves discoverable to brands looking to partner with them. Both sides work together as one unified system.",
  },
  {
    q: "How is IRL community marketing different from influencer marketing?",
    a: "Influencer marketing puts your brand in front of an audience through one person's reach. Community marketing puts your brand inside a tribe, a group of people who already share values, rituals, and trust. The difference isn't just the channel, it is the depth and precision of the activations.",
    plainAnswer: "Influencer marketing puts your brand in front of an audience through one person's reach. Community marketing puts your brand inside a tribe, a group of people who already share values, rituals, and trust. The difference isn't just the channel, it is the depth and precision of the activations.",
  },
  {
    q: "Can't I just reach out to communities manually?",
    a: "You can, but finding the right ones, briefing them, coordinating logistics, and getting data back takes weeks per campaign. That is the part we take off your hands: we source the communities, handle the briefing and the logistics, and come back with the results. You follow all of it in the app.",
    plainAnswer: "You can, but finding the right ones, briefing them, coordinating logistics, and getting data back takes weeks per campaign. That is the part we take off your hands: we source the communities, handle the briefing and the logistics, and come back with the results. You follow all of it in the app.",
  },
  {
    q: "What does \"IRL community activation\" mean concretely?",
    a: "It can be product gifting to a running club, sponsoring a ceramics studio event, partnering with a supper club as an ambassador program, or funding branded content for a book club.",
    plainAnswer: "It can be product gifting to a running club, sponsoring a ceramics studio event, partnering with a supper club as an ambassador program, or funding branded content for a book club.",
  },
  {
    q: "What communities are on ernest?",
    a: "Sweat clubs, smart clubs, hospitality clubs, fandom clubs: running crews, book clubs, supper clubs, cycling collectives, gaming communities and more, across France and growing.",
    plainAnswer: "Sweat clubs, smart clubs, hospitality clubs, fandom clubs: running crews, book clubs, supper clubs, cycling collectives, gaming communities and more, across France and growing.",
  },
  {
    q: "What kind of brands use ernest?",
    a: "Brands looking for new visibility opportunities beyond ads and influencers, especially consumer brands in beauty, wellness, food & beverage, sport, and fashion.",
    plainAnswer: "Brands looking for new visibility opportunities beyond ads and influencers, especially consumer brands in beauty, wellness, food & beverage, sport, and fashion.",
  },
];

const SPONSOR_OPTIONS = [
  {
    label: "Product",
    description: "Seed your product where your customers already gather, at their run club, supper club, or weekend meetup. Real hands-on trial beats another ad they'll scroll past.",
    tags: ["Seeding & Sampling", "Product Test & Panels"],
  },
  {
    label: "Events",
    description: "Fund the events your customers already care about, in person. The bar, the retreat, the afterparty. You become part of the memory.",
    tags: ["Event Sponsorship", "Community Sponsorship"],
  },
  {
    label: "Affiliation",
    description: "Reward the members who match your audience so they pick you first: early drops, discounts, VIP access. Turn your best fans into ambassadors who spread the word.",
    tags: ["Ambassador Program", "Affiliate Program"],
  },
  {
    label: "Content",
    description: "Amplify what every offline activation creates. Repurpose it into branded posts and community spotlights that keep reaching your target audience.",
    tags: ["UGC", "Branded Content"],
  },
];

const SPONSOR_ICONS: Record<string, typeof Sprout> = {
  Product: Sprout,
  Events: CalendarDays,
  Content: Camera,
  Affiliation: Award,
};

const ModuleWrapper = ({
  children,
  bgImage,
  contentClassName = "",
}: {
  children: React.ReactNode;
  bgImage: string;
  contentClassName?: string;
}) => (
  <div className="relative w-full mx-auto">
    {/* Background community image */}
    <div className="absolute inset-0 rounded-2xl overflow-hidden">
      <img src={bgImage} alt="" className="w-full h-full object-cover opacity-50" />
    </div>
    <div className={`relative z-10 flex-wrap md:flex-nowrap gap-1.5 flex-row flex justify-center items-center md:scale-100 ${contentClassName}`}>
      {children}
    </div>
  </div>
);

const DiscoverModule = () => (
  <ModuleWrapper bgImage={`${SB}/club15.jpg`} contentClassName="px-1 py-3 md:p-6 lg:p-8 h-[220px] md:min-h-[340px] overflow-hidden">
    <div className="absolute inset-0 m-auto h-fit md:static md:inset-auto md:m-0 flex gap-2 md:gap-3 w-full max-w-[440px] sm:max-w-[460px] items-center justify-center scale-[0.82] md:scale-100 xl:scale-[1.2] origin-center">
      {/* Left: Typology brief card */}
      <FloatingCard className="w-[160px] sm:w-[200px] px-2 py-3 md:p-3 shrink-0" delay={0.1}>
        <div className="flex justify-center mb-1">
          <img
            src="https://gcbavizlaxatiafdkaiy.supabase.co/storage/v1/object/public/community-images-homepage/Screenshot%202026-04-25%20at%2013.15.12.jpeg"
            alt="Typology SPF Summer 26"
            loading="lazy"
            className="w-16 h-16 rounded-full object-cover shadow-sm"
          />
        </div>
        <div className="flex justify-center">
          <img
            src="https://gcbavizlaxatiafdkaiy.supabase.co/storage/v1/object/public/community-images-homepage/typology.png"
            alt="Typology"
            loading="lazy"
            className="h-6 w-auto object-contain"
          />
        </div>
        <div className="flex justify-center mt-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[hsl(var(--ernest-neon)/0.3)] text-[8px] md:text-[9px] uppercase tracking-[0.12em] font-medium text-foreground">
            New SPF for Summer 26
          </span>
        </div>
        <div className="mt-2.5 space-y-1.5 flex flex-col items-center">
          {[
            { icon: Leaf, label: "Clean · 11 ingredients" },
            { icon: Sun, label: "SPF 50+ · Mineral" },
            { icon: ShieldCheck, label: "Dermatologist tested" },
          ].map(({ icon: Icon, label }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3, margin: MODULE_VIEWPORT_MARGIN }}
              transition={{ duration: 0.4, delay: 0.7 + i * 0.25 }}
              className="flex items-center gap-1.5 text-foreground/80"
            >
              <Icon className="w-3 h-3 text-[#584dff] shrink-0" strokeWidth={2} />
              <span className="font-light text-[10px] md:text-[11px]">{label}</span>
            </motion.div>
          ))}
        </div>
      </FloatingCard>

      {/* Right: Purple text + Values stacked */}
      <div className="flex flex-col gap-2.5 w-[160px] sm:w-[175px] shrink-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3, margin: MODULE_VIEWPORT_MARGIN }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="rounded-2xl p-2.5 bg-[#584dff]"
        >
          <p className="text-white text-[10px] md:text-[11px] leading-relaxed italic">
            <Sparkles className="inline-block align-[-2px] mr-1 w-3 h-3 text-white" strokeWidth={2} />
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3, margin: MODULE_VIEWPORT_MARGIN }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              "She reads every ingredient list, does pilates, shops at the bio market on sundays. She's exactly who this SPF was made for."
            </motion.span>
          </p>
        </motion.div>

        <FloatingCard className="px-2.5 py-2" delay={0.45}>
          <p className="text-[9px] md:text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1.5">Values</p>
          <ValueChips />
        </FloatingCard>
      </div>
    </div>
  </ModuleWrapper>
);

const EngageModule = () => (
  <ModuleWrapper bgImage={`${SB}/club3.jpg`} contentClassName="p-4 md:p-10 lg:p-12 h-[190px] md:min-h-[340px] overflow-hidden">
    {/* Desktop layout */}
    <div className="hidden md:flex items-center gap-1.5 xl:scale-[1.2]">
      <FloatingCard className="w-[200px] p-3.5" delay={0.1}>
        <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Activation formats</p>
        <ActivationFormats />
      </FloatingCard>

      <div className="flex flex-col gap-3">
        <FloatingCard className="w-[180px] p-3 ml-[15px]" delay={0.25}>
          <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1.5">Campaign</p>
          <p className="text-sm font-medium text-foreground">Summer SPF Launch</p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">Active</span>
            <span className="text-[10px] text-muted-foreground">3 activations</span>
          </div>
        </FloatingCard>

        <div className="flex -space-x-3 ml-2">
          {[
            { name: "Parenthèse", detail: "Seaside yoga retreat", delay: 1.0 },
            { name: "Les Fondatrices", detail: "Clean beauty\nedit", delay: 1.7 },
            { name: "Bien Commun", detail: "Ingredient masterclass", delay: 2.4 },
          ].map((a) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3, margin: MODULE_VIEWPORT_MARGIN }}
              transition={{ duration: 0.4, delay: a.delay }}
              className="w-[80px] h-[80px] rounded-full bg-white border-2 border-white shadow-md flex flex-col items-center justify-center text-center p-1.5 cursor-default"
              title={`${a.name} — ${a.detail}`}
            >
              <span className="text-[9.5px] font-semibold text-foreground leading-tight">{a.name}</span>
              <span className="text-[7.5px] leading-tight mt-0.5 text-[#584dff]">{a.detail}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>

    {/* Mobile layout: activation formats left, campaign+bubbles right */}
    <div className="md:hidden absolute inset-0 m-auto h-fit flex gap-2 items-start justify-center w-full scale-[0.82] origin-center">
      <FloatingCard className="w-[160px] p-3 shrink-0" delay={0.1}>
        <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Activation formats</p>
        <ActivationFormats compact />
      </FloatingCard>

      <div className="flex flex-col gap-2 shrink-0">
        <FloatingCard className="w-[155px] p-2.5" delay={0.25}>
          <p className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1">Campaign</p>
          <p className="text-[10px] font-medium text-foreground">Summer SPF Launch</p>
          <div className="flex items-center gap-1 mt-1">
            <span className="px-1.5 py-0.5 rounded-full text-[8px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">Active</span>
            <span className="text-[8px] text-muted-foreground">3 activations</span>
          </div>
        </FloatingCard>

        <div className="flex -space-x-1.5">
          {[
            { name: "Parenthèse", detail: "Seaside yoga retreat", delay: 1.0 },
            { name: "Les Fondatrices", detail: "Clean beauty\nedit", delay: 1.7 },
            { name: "Bien Commun", detail: "Ingredient masterclass", delay: 2.4 },
          ].map((a) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3, margin: MODULE_VIEWPORT_MARGIN }}
              transition={{ duration: 0.4, delay: a.delay }}
              className="w-[56px] h-[56px] rounded-full bg-white border-2 border-white shadow-md flex flex-col items-center justify-center text-center p-1 cursor-default"
            >
              <span className="text-[7px] font-semibold text-foreground leading-tight">{a.name}</span>
              <span className="text-[5.5px] leading-tight mt-0.5 text-[#584dff]">{a.detail}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </ModuleWrapper>
);

const MeasureModule = () => (
  <ModuleWrapper bgImage={`${SB}/club1.png`} contentClassName="p-4 md:p-10 lg:p-12 h-[190px] md:min-h-[340px] overflow-hidden">
    {/* Desktop layout */}
    <div className="hidden md:flex gap-3 items-start xl:scale-[1.2]">
      <div className="flex flex-col gap-3 items-start">
        <FloatingCard className="w-[190px] p-3" delay={0.1}>
          <p className="text-[9.5px] uppercase tracking-[0.12em] text-muted-foreground font-medium leading-tight whitespace-nowrap">Genuine Interest Rate</p>
          <p className="text-xl font-medium text-foreground mt-0.5"><CountUp to={78} suffix="%" delay={0.5} /></p>
          <div className="w-full h-1 rounded-full bg-muted mt-1.5">
            <GrowBar pct={78} />
          </div>
        </FloatingCard>

        <FloatingCard className="w-[190px] p-3" delay={0.25}>
          <div className="space-y-1.5">
            {MEASURE_METRICS.map((m) => (
              <div key={m.label} className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">{m.label}</span>
                <span className="text-sm font-medium text-foreground"><CountUp to={m.to} decimals={m.decimals} suffix={m.suffix} group={m.group} delay={0.7} /></span>
              </div>
            ))}
          </div>
        </FloatingCard>
      </div>

      <FloatingCard className="w-[215px] p-3 border-emerald-200/60" delay={0.4}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <TrendingUp className="w-3 h-3 text-emerald-600" strokeWidth={2} />
          <span className="text-[11px] uppercase tracking-[0.15em] text-emerald-600 font-medium">Outperformed</span>
        </div>
        <p className="text-[11px] text-foreground/80 leading-relaxed">
          Your activation outperformed <span className="font-semibold text-foreground"><CountUp to={92} suffix="%" delay={1} /></span> of activations in clean beauty.
        </p>
        <p className="text-[#5b4fff] font-medium text-[11px] mt-1.5 cursor-pointer">See How →</p>
      </FloatingCard>
    </div>

    {/* Mobile layout: GIR + Reach left, Outperformed right */}
    <div className="md:hidden flex gap-2 items-start w-full justify-center scale-[0.82] origin-center">
      <div className="flex flex-col gap-2 shrink-0">
        <FloatingCard className="w-[150px] p-2.5" delay={0.1}>
          <p className="text-[8px] uppercase tracking-[0.15em] text-muted-foreground font-medium leading-tight">Genuine Interest Rate</p>
          <p className="text-base font-medium text-foreground mt-0.5"><CountUp to={78} suffix="%" delay={0.5} /></p>
          <div className="w-full h-1 rounded-full bg-muted mt-1">
            <GrowBar pct={78} />
          </div>
        </FloatingCard>

        <FloatingCard className="w-[150px] p-2.5" delay={0.25}>
          <div className="space-y-1">
            {MEASURE_METRICS.map((m) => (
              <div key={m.label} className="flex items-center justify-between">
                <span className="text-[9px] text-muted-foreground">{m.label}</span>
                <span className="text-[10px] font-medium text-foreground"><CountUp to={m.to} decimals={m.decimals} suffix={m.suffix} group={m.group} delay={0.7} /></span>
              </div>
            ))}
          </div>
        </FloatingCard>
      </div>

      <FloatingCard className="w-[160px] p-2.5 border-emerald-200/60 shrink-0" delay={0.4}>
        <div className="flex items-center gap-1 mb-1">
          <TrendingUp className="w-2.5 h-2.5 text-emerald-600" strokeWidth={2} />
          <span className="text-[9px] uppercase tracking-[0.15em] text-emerald-600 font-medium">Outperformed</span>
        </div>
        <p className="text-[9px] text-foreground/80 leading-relaxed">
          Your activation outperformed <span className="font-semibold text-foreground"><CountUp to={92} suffix="%" delay={1} /></span> of activations in clean beauty.
        </p>
        <p className="text-[#5b4fff] font-medium text-[9px] mt-1 cursor-pointer">See How →</p>
      </FloatingCard>
    </div>
  </ModuleWrapper>
);

const HomePage = () => {
  const [posts, setPosts] = useState<BlogPostPreview[]>([]);
  const [contactOpen, setContactOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // wait a tick for content to render
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, cover_image_url, author_name, published_at, tags")
        .eq("published", true)
        .order("published_at", { ascending: false })
        .limit(3);
      if (data) setPosts(data as BlogPostPreview[]);
    })();
  }, []);

  return (
    <div className="min-h-screen text-foreground bg-[#f8f7f5]">
      <Helmet>
        <title>ernest – IRL community marketing & sponsorship for brands</title>
        <meta name="description" content="Run, measure, and scale IRL community partnerships across 25,000 clubs and communities. Ernest helps brands make community a measurable growth channel." />
        <link rel="canonical" href="https://ernest-app.com/" />
        <meta property="og:title" content="ernest – IRL community marketing & sponsorship for brands" />
        <meta property="og:description" content="Run, measure, and scale IRL community partnerships across 25,000 clubs and communities. Ernest helps brands make community a measurable growth channel." />
        <meta property="og:url" content="https://ernest-app.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://ernest-app.com${socialPreview.url}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Ads get skipped, communities don't" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ernest – IRL community marketing & sponsorship for brands" />
        <meta name="twitter:description" content="Run, measure, and scale IRL community partnerships across 25,000 clubs and communities. Ernest helps brands make community a measurable growth channel." />
        <meta name="twitter:image" content={`https://ernest-app.com${socialPreview.url}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "ernest",
          url: "https://ernest-app.com",
          logo: "https://ernest-app.com/ernest-wordmark.png",
          description: "Run, measure, and scale IRL community partnerships across 25,000 clubs and communities. Ernest helps brands make community a measurable growth channel.",
          sameAs: ["https://www.linkedin.com/company/ernest-app/"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "ernest",
          url: "https://ernest-app.com"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map(({ q, plainAnswer }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: plainAnswer },
          })),
        })}</script>
      </Helmet>



      {/* Nav */}
      <header className="static md:sticky md:top-0 z-50 backdrop-blur-md border-b-0 md:border-b border-border/40 bg-[#f8f7f5]">

        <div className="max-w-7xl mx-auto px-6 lg:px-10 lg:pr-0 h-16 flex items-center justify-between">
          <Link to="/">
            <img src="/ernest-wordmark.png" alt="ernest" width={140} height={36} fetchPriority="high" decoding="async" className="h-[28px] w-auto ml-[-2px] md:h-9 md:ml-0" />
          </Link>
          {/* Center nav links */}
          <nav className="hidden md:flex items-center absolute left-1/2 -translate-x-1/2 gap-5 lg:gap-[50px]">
            <a href="/#how" className="text-foreground/60 font-normal transition-colors duration-300 hover:text-foreground text-sm lg:text-lg whitespace-nowrap">How it works</a>
            <IndustriesDropdown />
            <Link to="/blog" className="text-foreground/60 font-normal transition-colors duration-300 hover:text-foreground text-sm lg:text-lg whitespace-nowrap">Resources</Link>
          </nav>
          <div className="flex items-center gap-4 md:pr-4 lg:pr-[40px] md:gap-3 lg:gap-[35px]">
            <Link
              to="/login"
              className="no-underline transition-colors duration-300 text-[18px] md:text-sm lg:text-lg text-[#5b4fff] font-medium md:font-normal"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[hsl(var(--ernest-neon))] text-foreground border border-black/10 hover:scale-[1.02] transition whitespace-nowrap no-underline text-sm lg:text-base font-normal px-4 py-1.5"
            >
              Get started <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#f8f7f5] pt-12 pb-8 md:pt-16 md:pb-10 lg:pt-20 lg:pb-12">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[hsl(var(--ernest-neon))] opacity-10 blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-[40px] mt-0 md:mt-[20px]">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-5xl mx-auto pb-[10px] text-center">


            <h1 className="mt-4 md:mt-6 text-[9.8vw] md:text-6xl tracking-[-0.03em] leading-[1.2] md:leading-[1.1] lg:text-7xl font-[550] text-center" style={{ color: '#000000' }}>
              <span className="whitespace-nowrap">
                Ads get skipped,<br />
                communities don't.
              </span>
            </h1>
            <p className="mt-4 md:mt-8 text-base md:text-xl text-muted-foreground max-w-none leading-relaxed font-light">
              <span className="md:hidden">
                Reach the right people through<br />
                the communities they love.<br />
                Run, measure, and scale activations<br />
                across Europe's largest network of communities.
              </span>
              <span className="hidden md:inline">
                Reach the right people through the communities they love.<br />
                Run, measure, and scale activations across Europe's largest network of communities.
              </span>
            </p>
            <div className="mt-6 md:mt-10 flex flex-row-reverse gap-3 md:gap-6 items-center justify-center">
              <Link
                to="/signup"
                className="group inline-flex justify-center items-center gap-2 px-5 md:px-6 py-3 md:py-3.5 rounded-full bg-[hsl(var(--ernest-neon))] text-foreground font-medium text-sm md:text-lg border border-black/10 hover:scale-[1.02] transition whitespace-nowrap no-underline"
              >
                Get started
              </Link>
              <a
                href="https://cal.com/marguerite-japy-u27fez/discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex justify-center items-center gap-2 px-5 md:px-6 py-3 md:py-3.5 rounded-full bg-gradient-to-r from-[#5b4fff]/10 to-[#8b7fff]/6 border border-[#5b4fff]/15 backdrop-blur-sm text-[#5b4fff] font-medium text-sm md:text-lg no-underline hover:from-[#5b4fff]/15 hover:to-[#8b7fff]/10 hover:scale-[1.02] transition whitespace-nowrap"
              >
                Talk to us
              </a>
            </div>



          </motion.div>
        </div>

        {/* STAT BAR — France launch proof points */}
        <div className="max-w-5xl mx-auto px-6 lg:px-[40px] mt-8 md:mt-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col gap-2.5 sm:grid sm:grid-cols-3 sm:gap-6 md:gap-8 sm:text-center"
          >
            <div className="flex flex-row sm:flex-col items-center justify-center gap-2">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#584dff] shrink-0" strokeWidth={1.75} />
              <div className="text-[14px] sm:text-[15px] md:text-[17px] font-medium tracking-tight text-foreground">
                <span className="sm:block">Live in France,</span>{" "}
                <span className="sm:block">expanding across Europe</span>
              </div>
            </div>
            <div className="flex flex-row sm:flex-col items-center justify-center gap-2">
              <Search className="w-4 h-4 sm:w-5 sm:h-5 text-[#584dff] shrink-0" strokeWidth={1.75} />
              <div className="text-[14px] sm:text-[15px] md:text-[17px] font-medium tracking-tight text-foreground">
                <span className="sm:block">25 000+ communities mapped</span>{" "}
                <span className="sm:block">across 4 countries</span>
              </div>
            </div>
            <div className="flex flex-row sm:flex-col items-center justify-center gap-2">
              <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-[#584dff] shrink-0" strokeWidth={1.75} />
              <div className="text-[14px] sm:text-[15px] md:text-[17px] font-medium tracking-tight text-foreground">
                <span className="sm:block">Proprietary data</span>{" "}
                <span className="sm:block">on IRL communities</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scrolling community marquee */}
        <div className="w-full mt-12 md:mt-16 overflow-hidden">
          <div
            className="flex gap-3 marquee-track"
            style={{
              width: "max-content",
            }}
          >
            {[...Array(2)].map((_, dupeIdx) => (
              [
                { src: `${SB}/club14.jpg`, caption: "WOMEN CYCLING CLUB · NICE", pos: "center 40%", w: 250 },
                { src: `${SB}/club24.jpg`, caption: "FOUNDERS MEETUP · LYON", pos: "center 45%", w: 270 },
                { src: `${SB}/communaute.jpg`, caption: "RUNNING CLUB · PARIS", pos: "center 30%", w: 300 },
                { src: `${SB}/club15.jpg`, caption: "OUTDOOR FITNESS CLUB · BORDEAUX", pos: "center 35%", w: 340 },
                { src: `${SB}/club1.png`, caption: "PADEL CLUB · TOULOUSE", pos: "center 30%", w: 320 },
                { src: `${SB}/club25.jpg`, caption: "DEBATE CLUB · PARIS", pos: "center 40%", w: 260 },
                
                { src: `${SB}/club6.jpg`, caption: "BOOK CLUB · LILLE", pos: "center 50%", w: 240 },
                { src: `${SB}/commu%20fond.jpg`, caption: "LONGEVITY & WELLNESS CLUB · MARSEILLE", pos: "center 65%", w: 290 },
              ].map((tile, i) => (
                <div
                  key={`${dupeIdx}-${i}`}
                  className="group relative overflow-hidden flex-shrink-0"
                  style={{
                    width: tile.w,
                    height: "clamp(190px, 19vw, 260px)",
                    borderRadius: 14,
                    boxShadow: "0 6px 20px -6px rgba(20,20,40,0.15)",
                  }}
                >
                  <img
                    src={tile.src}
                    alt=""
                    width={tile.w}
                    height={260}
                    loading={dupeIdx === 0 && i < 4 ? "eager" : "lazy"}
                    fetchPriority={dupeIdx === 0 && i === 0 ? "high" : "auto"}
                    decoding={dupeIdx === 0 && i < 4 ? "sync" : "async"}
                    className="w-full h-full object-cover transition-transform duration-[550ms] ease-out group-hover:scale-[1.06]"
                    style={{ objectPosition: tile.pos }}
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.3) 100%)", borderRadius: 14 }} />
                  <div className="absolute bottom-2 left-2">
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 10,
                        textTransform: "uppercase" as const,
                        letterSpacing: "0.06em",
                        color: "#fff",
                        background: "rgba(0,0,0,0.45)",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                        padding: "3px 7px",
                        borderRadius: 6,
                        fontWeight: 500,
                      }}
                    >
                      {tile.caption}
                    </span>
                  </div>
                </div>
              ))
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 steps with floating card compositions */}
      <section id="how" className="relative overflow-hidden bg-foreground text-background rounded-[40px] py-16 md:pt-[100px] md:pb-[100px] lg:pt-[130px] lg:pb-[130px] mt-[80px] md:mt-[90px]">
        <div className="absolute inset-0 z-0 bg-foreground" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-[90px]">

          <div className="mb-[25px] md:mb-[80px] md:pb-[20px]">
            <div className="hidden md:block uppercase tracking-[0.2em] mb-4 text-center text-base font-normal text-[#584dff]">HOW ERNEST WORKS</div>
            <h2 className="md:text-3xl font-medium tracking-tight leading-[1.05] lg:text-[42px] text-center">
              <span className="md:hidden text-3xl">How ernest works</span>
              <span className="hidden md:inline text-4xl font-medium pb-0">From your goals to proof it worked.</span>
            </h2>
          </div>

          {/* Step 01 — Discover */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center mb-16 md:mb-28">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
            >
              <span className="hidden md:block text-2xl font-medium text-background/40 mb-3">01</span>
              <h3 className="text-xl md:text-3xl lg:text-4xl font-medium tracking-tight mb-4 text-[color-mix(in_srgb,hsl(var(--ernest-neon))_80%,white)]"><span className="md:hidden text-xl font-medium text-background/40 mr-2">01</span>Discover</h3>
              <p className="text-background/80 font-light leading-relaxed max-w-md text-base md:text-lg">
                Filter communities by values, location, themes, member intent, and profiles. Understand who your people are, which tribes they belong to, and whether your brand has a place among them.
              </p>
            </motion.div>
            <DiscoverModule />
          </div>

          {/* Step 02 — Engage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center mb-16 md:mb-28">
            <div className="md:order-2">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7 }}
              >
                <span className="hidden md:block text-2xl font-medium text-background/40 mb-3">02</span>
                <h3 className="text-xl md:text-3xl lg:text-4xl font-medium tracking-tight mb-4 text-[color-mix(in_srgb,hsl(var(--ernest-neon))_80%,white)]"><span className="md:hidden text-xl font-medium text-background/40 mr-2">02</span>Activate</h3>
                <p className="text-background/80 font-light leading-relaxed max-w-md text-base md:text-lg">
                  Choose your format: gifting, event sponsorship, ambassador programs, or branded content. Brief the community leaders, coordinate logistics, and run campaigns from one workspace.
                </p>
              </motion.div>
            </div>
            <div className="md:order-1">
              <EngageModule />
            </div>
          </div>

          {/* Step 03 — Measure */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
            >
              <span className="hidden md:block text-2xl font-medium text-background/40 mb-3">03</span>
              <h3 className="text-xl md:text-3xl lg:text-4xl font-medium tracking-tight mb-4 text-[color-mix(in_srgb,hsl(var(--ernest-neon))_80%,white)]"><span className="md:hidden text-xl font-medium text-background/40 mr-2">03</span>Measure</h3>
              <p className="text-background/80 font-light leading-relaxed max-w-md text-base md:text-lg">
                Track sentiment, purchase intent, and attributed conversions per activation. Benchmark community spend against paid and influencer channels. Make community a measurable growth channel.
              </p>
            </motion.div>
            <MeasureModule />
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 md:mt-24 flex justify-center">
            <Link
              to="/signup"
              className="group inline-flex items-center gap-2 px-6 py-3 md:py-3.5 rounded-full bg-[hsl(var(--ernest-neon))] text-foreground font-medium text-[18px] md:text-base border border-black/10 hover:scale-[1.02] transition whitespace-nowrap no-underline"
            >
              Get started
            </Link>
          </div>

        </div>
      </section>

      {/* WHY COMMUNITY MARKETING — problem framing before the offer */}
      <section className="relative bg-[#f8f7f5] py-16 md:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-[90px]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
          >
            <div className="uppercase tracking-[0.2em] mb-4 text-base font-normal text-[#584dff]">The shift</div>
            <h2 className="text-3xl md:text-4xl tracking-[-0.03em] leading-[1.1] text-foreground font-medium">
              How people discover brands is changing.
            </h2>
          </motion.div>

          {/* One panel rather than two floating cards. Framed as complementary to
              paid, not a replacement for it. No vertical rule: the right half
              fades from white to lilac, starting a little left of the seam, and three chevrons of growing intensity
              sit on the seam, on the rule between the left half's two stats. Stats sit in ruled rows with
              the source on its own line; captions are kept short enough to hold
              one line on desktop. The AI note is the panel's footer row. */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="relative grid grid-cols-1 md:grid-cols-2 rounded-3xl border border-foreground/10 bg-white md:bg-[linear-gradient(to_right,#fff_30%,#ece8fd_100%)] overflow-hidden"
          >
            {[
              {
                label: "Paid & influencer",
                title: "Reach is getting pricier.",
                stats: [
                  { value: "+60%", text: "Paid social ad costs, last 5 years", source: "HubSpot" },
                  { value: "−65%", text: "Influencer engagement since 2022", source: "Sprout Social" },
                ],
                panel: "",
                chevrons: true,
                labelColor: "text-foreground/45",
                valueColor: "text-foreground",
              },
              {
                label: "Community marketing",
                title: "Trust that's already there.",
                stats: [
                  { value: "89%", text: "Trust peer recommendations over ads", source: "Nielsen" },
                  { value: "#1", text: "Word-of-mouth as a purchase driver", source: "McKinsey" },
                ],
                panel: "bg-gradient-to-b md:bg-none from-white to-[#ece8fd]",
                labelColor: "text-[#584dff]",
                valueColor: "text-[#584dff]",
              },
            ].map((col) => (
              <div key={col.label} className={`p-6 md:p-8 lg:px-10 ${col.panel}`}>
                <div className={`text-sm font-medium mb-2 ${col.labelColor}`}>{col.label}</div>
                <p className="text-xl md:text-2xl font-medium tracking-tight text-foreground mb-6 md:mb-8">{col.title}</p>
                {col.stats.map((s, i) => (
                  <div key={s.value} className="relative flex items-center gap-5 border-t border-foreground/10 py-5 last:pb-0">
                    {col.chevrons && i === 1 && (
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 62 40"
                        className="pointer-events-none absolute right-0 top-0 z-10 hidden h-8 w-12 -translate-y-1/2 translate-x-1/2 stroke-foreground md:-right-8 md:block lg:-right-10"
                        fill="none"
                                                strokeWidth="5"
                      >
                        <path d="M2 3 L14 20 L2 37" strokeOpacity="0.15" />
                        <path d="M22 3 L34 20 L22 37" strokeOpacity="0.35" />
                        <path d="M42 3 L54 20 L42 37" strokeOpacity="0.65" />
                      </svg>
                    )}
                    <div className={`w-[5.75rem] md:w-[7.5rem] shrink-0 text-[32px] md:text-[40px] leading-none font-medium tracking-tight tabular-nums ${col.valueColor}`}>
                      {s.value}
                    </div>
                    <div>
                      <p className="text-foreground/75 text-[15px] leading-snug">{s.text}</p>
                      <p className="mt-1 text-xs text-foreground/40">{s.source}</p>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* AI angle: no stat on purpose, so nothing here needs a source. */}
            <div className="flex items-center gap-4 border-t border-foreground/10 bg-white px-6 py-5 md:col-span-2 md:px-8 lg:px-10">
              <div>
                <p className="text-xl md:text-2xl font-medium tracking-tight text-foreground">What about AI?</p>
                <p className="mt-2 text-[15px] leading-snug text-foreground/75">
                  Ask ChatGPT what to buy, and it draws on what communities have to say: club pages, forums, event recaps, reviews. IRL experiences create online signals that shape how AI discovers and recommends brands.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ACTIVATION TYPES — what brands run with communities */}
      <section className="relative overflow-hidden rounded-[40px] bg-foreground text-background py-16 md:py-20 lg:py-24 mt-6 md:mt-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-[90px]">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
            >
              <div className="uppercase tracking-[0.2em] mb-4 text-base font-normal text-[#584dff]">Sponsorship formats</div>
              <h2 className="text-3xl md:text-4xl tracking-[-0.03em] leading-[1.1] text-background font-medium">
                Turn real-world communities into a powerful growth channel.
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SPONSOR_OPTIONS.map((opt, i) => {
                const Icon = SPONSOR_ICONS[opt.label];
                return (
                <motion.div
                  key={opt.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="relative rounded-3xl bg-[#fbfaf8] border border-border/60 shadow-[0_8px_20px_-10px_rgba(20,20,40,0.15)] p-6 md:p-7"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#584dff] flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight text-foreground">
                      {opt.label}
                    </h3>
                  </div>
                  <p className="mt-3 text-foreground/70 font-light leading-relaxed">{opt.description}</p>
                  <div className={`mt-4 flex gap-2 ${opt.label === "Content" ? "flex-col items-start" : "flex-wrap"}`}>
                    {opt.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-[#584dff]/10 px-3 py-1.5 text-[13px] font-medium text-[#584dff]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>
                );
              })}
            </div>
          </div>

          <div className="relative z-[60] mt-10 md:mt-16 flex flex-col md:flex-row-reverse gap-3 md:gap-12 items-center justify-center">
            <Link
              to="/signup"
              className="group inline-flex justify-center items-center gap-2 px-6 py-3 md:py-3.5 rounded-full bg-[hsl(var(--ernest-neon))] text-foreground font-medium text-[18px] md:text-lg border border-black/10 hover:scale-[1.02] transition whitespace-nowrap no-underline"
            >
              Get started
            </Link>
            <a
              href="https://cal.com/marguerite-japy-u27fez/discovery-call"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex justify-center items-center gap-2 px-6 py-3 md:py-3.5 rounded-full bg-gradient-to-r from-[#5b4fff]/10 to-[#8b7fff]/6 border border-[#5b4fff]/15 backdrop-blur-sm text-[#5b4fff] font-medium text-[18px] md:text-lg no-underline hover:from-[#5b4fff]/15 hover:to-[#8b7fff]/10 hover:scale-[1.02] transition"
            >
              Talk to us
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="mt-3 md:mt-0 relative overflow-hidden scroll-mt-20 bg-[#f8f7f5] py-16 md:py-24 lg:py-28 pt-[120px] md:pt-[170px] pb-[100px] md:pb-[120px]">
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <div className="hidden md:inline-flex mx-auto mb-4 items-center uppercase tracking-[0.18em] font-normal text-base text-[#584dff]">
            FIRST MOVERS DON'T SHARE THEIR EDGE
          </div>
          <h2 className="md:mx-auto tracking-[-0.03em] leading-[1.1] text-3xl md:text-4xl lg:text-4xl font-medium mb-6 md:whitespace-nowrap">
            <span className="md:hidden">For brands looking to work with communities</span>
            <span className="hidden md:inline">For brands looking to work with communities.</span>
          </h2>
          <p className="mb-4 md:mx-auto max-w-xl text-foreground text-base md:text-lg font-medium">
            Tell us who you want to reach. We find the right communities.
          </p>
          <p className="md:mx-auto max-w-xl text-muted-foreground text-base md:text-lg font-light whitespace-pre-line">
            The best brands of 2026 show up where it matters most: the Sunday run club, the early morning surf crew, the supper club. Join ernest and make belonging and connection your edge.
          </p>
          <div className="mt-8 flex flex-col gap-3 md:gap-4 items-center justify-center">
            {/* Mobile: hero-style neon CTA */}
            <Link
              to="/signup"
              className="md:hidden group inline-flex justify-center items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--ernest-neon))] text-foreground font-medium text-[18px] border border-black/10 hover:scale-[1.02] transition whitespace-nowrap no-underline"
            >
              Get started
            </Link>
            {/* Desktop: original style */}
            <Link
              to="/signup"
              className="hidden md:inline-flex group items-center gap-2 px-6 py-3.5 rounded-full font-medium md:text-lg border border-black/10 hover:scale-[1.02] transition bg-[hsl(var(--ernest-neon))] text-foreground no-underline"
            >
              Get started
            </Link>
          </div>

          {/* Logo social proof */}
          <div className="mt-6 md:mt-4 flex flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-x-6 sm:gap-x-10 md:gap-x-14 gap-y-1 sm:gap-y-10 md:gap-y-14 flex-wrap">
              {[
                "/logos/asphalte.png",
                "/logos/danone.png",
                "/logos/typology.png",
                "/logos/okr.png",
                "/logos/horace.png",
              ].map((src, i) => {
                let sizeClass = "h-[24px] sm:h-[28px] md:h-[38px]";
                let offsetClass = "";
                let marginClass = "";
                if (src.includes("typology")) {
                  sizeClass = "h-[44px] sm:h-[52px] md:h-[68px]";
                } else if (src.includes("danone")) {
                  sizeClass = "h-[38px] sm:h-[40px] md:h-[52px]";
                  offsetClass = "mt-1.5 sm:mt-2 md:mt-2.5";
                  marginClass = "mr-2 sm:mr-4 md:mr-6";
                } else if (src.includes("asphalte")) {
                  sizeClass = "h-[20px] sm:h-[23px] md:h-[32px]";
                  marginClass = "mr-2 sm:mr-4 md:mr-6";
                } else if (src.includes("okr")) {
                  sizeClass = "h-[21px] sm:h-[27px] md:h-[29px]";
                } else if (src.includes("horace")) {
                  sizeClass = "h-[24px] sm:h-[32px] md:h-[33px]";
                }
                return (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className={`${sizeClass} ${offsetClass} ${marginClass} w-auto object-contain opacity-60 grayscale`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section id="faq" className="bg-[#f8f7f5] pt-4 pb-16 md:pt-8 md:pb-24 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <header className="mb-12 md:mb-16 text-center">
            <span className="hidden md:inline-flex items-center gap-2 uppercase tracking-[0.18em] font-normal text-base text-[#584dff]">
              FAQ
            </span>
            <h2 className="mt-6 text-3xl md:text-4xl tracking-[-0.03em] leading-[1.1] text-foreground font-medium lg:text-4xl">
              <span className="md:hidden">Frequently asked questions</span>
              <span className="hidden md:inline">Frequently asked questions.</span>
            </h2>
          </header>

          <div className="flex flex-col gap-3 md:gap-4">
            {FAQ_ITEMS.map(({ q, a }) => (
              <article key={q} className="border-b border-border/60 pb-5">
                <details className="group">
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                    <h3 className="text-lg md:text-xl font-medium text-foreground tracking-tight leading-snug">
                      {q}
                    </h3>
                    <span
                      aria-hidden="true"
                      className="mt-1 shrink-0 text-2xl leading-none text-muted-foreground transition-transform duration-200 group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <div className="mt-4 space-y-4 text-base text-muted-foreground font-light leading-relaxed md:text-lg">
                    {a}
                  </div>
                </details>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Talk to us — below FAQ */}
      <section className="bg-[#f8f7f5] pb-16 md:pb-24 text-center">
        <a
          href="https://cal.com/marguerite-japy-u27fez/discovery-call"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex justify-center items-center gap-2 px-6 py-3 md:py-3.5 rounded-full bg-gradient-to-r from-[#5b4fff]/15 to-[#8b7fff]/10 border border-[#5b4fff]/25 backdrop-blur-sm text-[#5b4fff] font-medium text-[18px] md:text-lg no-underline hover:from-[#5b4fff]/20 hover:to-[#8b7fff]/15 hover:scale-[1.02] transition"
        >
          Talk to us
        </a>
      </section>

      {/* Footer */}
      <footer className="mt-[39px] mb-6 md:mt-[55px] md:mb-10 mx-4 md:mx-20 lg:mx-32 xl:mx-56 rounded-3xl bg-foreground text-background py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-center justify-between">
            <img src="/ernest-wordmark-white.png" alt="ernest" className="h-7 md:h-8 w-auto object-contain" style={{ maxWidth: 130 }} />
            <div className="flex items-center gap-4 md:gap-5">
              <a href="https://www.linkedin.com/company/ernest-app/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-background hover:text-background/70 transition-colors">
                <Linkedin className="w-6 h-6 md:w-7 md:h-7" strokeWidth={1.5} />
              </a>
              <a href="mailto:contact@ernest-app.com" aria-label="Email" className="inline-flex text-background hover:text-background/70 transition-colors">
                <Mail className="w-6 h-6 md:w-7 md:h-7" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-background/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="order-last md:order-none text-background/50 text-sm">© {new Date().getFullYear()} ernest</p>
            <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-8 text-sm text-background/70">
              <Link to="/industries" className="hover:text-background transition-colors">Industries</Link>
              <Link to="/blog" className="hover:text-background transition-colors">Resources</Link>
              <Link to="/login" className="hover:text-background transition-colors">Login</Link>
              <a href="https://kithe.club" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-background transition-colors">
                For communities
              </a>
              <a href="https://rigorous-freeze-883.notion.site/Terms-and-Conditions-of-Use-ERNEST-398bb534f58880a3aa84caa360f416e5?source=copy_link" target="_blank" rel="noopener noreferrer" className="hover:text-background transition-colors">T&C</a>
              <a href="https://rigorous-freeze-883.notion.site/Privacy-Policy-ERNEST-398bb534f5888021bfb9faad96b4f8b0?source=copy_link" target="_blank" rel="noopener noreferrer" className="hover:text-background transition-colors">Privacy Policy</a>
              <button type="button" onClick={openCookieSettings} className="hover:text-background transition-colors">Cookie settings</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating contact widget */}
      <div className="fixed bottom-5 right-5 md:right-6 z-40 flex flex-col items-end gap-3">
        {contactOpen && (
          <div className="w-[280px] sm:w-[320px] rounded-2xl bg-white shadow-[0_12px_40px_-8px_rgba(20,20,40,0.25)] border border-border/60 p-5">
            <div className="flex items-start justify-between mb-3">
              <span className="text-[17px] font-medium tracking-tight text-foreground">Questions about ernest?</span>
              <button
                type="button"
                onClick={() => setContactOpen(false)}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-base text-muted-foreground font-light leading-relaxed mb-4">
              Reach out anytime, we're happy to help you get started.
            </p>
            <a
              href="mailto:contact@ernest-app.com"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#292939] text-white text-base font-medium px-4 py-2.5 hover:opacity-90 transition w-full"
            >
              <MessageCircle className="w-4 h-4" />
              contact@ernest-app.com
            </a>
          </div>
        )}
        <button
          type="button"
          onClick={() => setContactOpen((v) => !v)}
          aria-label="Contact us"
          className="w-14 h-14 rounded-full bg-[#584dff] text-white flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(20,20,40,0.35)] hover:scale-105 transition"
        >
          {contactOpen ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

export default HomePage;
